import { Inject, Injectable } from '@angular/core';
import * as SentryAngular from '@sentry/angular';
import { isEqual, last } from 'lodash';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';
import { environment } from '../../environments/environment';
import { LOCAL_STORAGE } from '../data/storage';
import { AppConfigurationError } from './app-configuration-error';

export interface AppConfig {
  shopName: string;
  apiKey: string;
  posApiKey: string;
  moneyFormat: string;
  onboarding: boolean; // Not Used yet
  iOSApp: string; // Not used yet
  pusherApiKey: string;
  locale: string;
  debug: boolean;
  cashBoxUuid?: string;
}

export const DEFAULT_LOCALE = 'de';

function isValidConfiguration(parsedConfig: unknown): parsedConfig is AppConfig {
  if (typeof parsedConfig !== 'object' || parsedConfig === null) {
    return false;
  }

  const REQUIRED_KEYS: (keyof AppConfig)[] = ['shopName', 'apiKey', 'posApiKey', 'moneyFormat', 'onboarding', 'iOSApp', 'pusherApiKey'];

  return REQUIRED_KEYS.every(key => key in parsedConfig);
}

function parseConfigurationString(encodedConfig: string): unknown {
  try {
    const decoded = atob(encodedConfig);
    return JSON.parse(decoded);
  } catch {
    throw new AppConfigurationError(`Could not parse received configuration string: ${encodedConfig}`);
  }
}

function decodeApiConfig(encodedConfig: string): AppConfig {
  const parsedConfig = parseConfigurationString(encodedConfig);

  if (!isValidConfiguration(parsedConfig)) {
    throw new AppConfigurationError(
      `Parsed configuration string is not a valid app configuration, ${JSON.stringify(parsedConfig)}`
    );
  }
  const defaultLocale = { locale: DEFAULT_LOCALE };
  const defaultDebug = { debug: true };
  return { ...defaultLocale, ...defaultDebug, ...parsedConfig };
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly STORAGE_CONFIG_KEY = 'app_config';
  private _config = new BehaviorSubject<AppConfig | null>(null);

  private get config() {
    return this._config.getValue();
  }

  private set config(conf: AppConfig | null) {
    this._config.next(conf);
  }

  configChange: Observable<AppConfig | null> = this._config.pipe(distinctUntilChanged(isEqual));

  constructor(@Inject(LOCAL_STORAGE) private readonly storage: Storage) {
  }

  storeAppConfig(configString: string) {
    this.setConfig(configString);
    this.storage.setItem(this.STORAGE_CONFIG_KEY, configString);
  }

  isConfigured(): boolean {
    return !!this.config;
  }

  initConfigFromUrl(url: string): boolean {
    const receivedConfig = this.getConfigStringFromUrl(url);
    if (receivedConfig) {
      this.storeAppConfig(receivedConfig);
      return true;
    }
    return false;
  }

  initConfig(): void {
    // First try to parse the config from the URL
    const url = location.href;
    if (this.initConfigFromUrl(url)) {
      return;
    }

    // If not received via URL, pick it from storage
    const storedConfig = this.storage.getItem(this.STORAGE_CONFIG_KEY) || environment.envConfig;

    if (!storedConfig) {
      return;
    }

    if (!isValidConfiguration(parseConfigurationString(storedConfig))) {
      this.clearConfig();
      return;
    }

    this.setConfig(storedConfig);
  }

  getConfig(): AppConfig | null {
    return this.config;
  }

  clearConfig(): void {
    this.storage.removeItem(this.STORAGE_CONFIG_KEY);
    this.config = null;
  }

  private setConfig(configString: string) {
    this.config = decodeApiConfig(configString);
    SentryAngular.setExtra('config', this.config);
  }

  private getConfigStringFromUrl(url: string): string | undefined {
    if (!url.includes('/config/')) {
      return;
    }
    const configValue = last(url.split('/config/'));
    if (!configValue) {
      throw new AppConfigurationError('No configuration received in query parameters.');
    }
    return configValue;
  }
}
